import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

interface ButtonProps extends Omit<HTMLMotionProps<"button">, 'children'> {
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  is_loading?: boolean;
  icon_left?: React.ReactNode;
  icon_right?: React.ReactNode;
  full_width?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  is_loading = false,
  icon_left,
  icon_right,
  full_width = false,
  className = '',
  disabled,
  ...props
}) => {
  const base_classes = 'inline-flex items-center justify-center font-medium rounded-lg transition-smooth focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variant_classes = {
    primary: 'bg-forest-green text-white hover:bg-dark-forest focus:ring-forest-green shadow-sm',
    secondary: 'bg-gray-200 text-charcoal hover:bg-gray-300 focus:ring-gray-400',
    success: 'bg-success text-white hover:bg-green-600 focus:ring-success shadow-sm',
    danger: 'bg-error text-white hover:bg-red-600 focus:ring-error shadow-sm',
    ghost: 'bg-transparent text-forest-green hover:bg-forest-green hover:bg-opacity-10 focus:ring-forest-green',
    outline: 'bg-transparent border-2 border-forest-green text-forest-green hover:bg-forest-green hover:text-white focus:ring-forest-green'
  };
  
  const size_classes = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5'
  };
  
  const width_class = full_width ? 'w-full' : '';
  
  const combined_classes = `${base_classes} ${variant_classes[variant]} ${size_classes[size]} ${width_class} ${className}`;
  
  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      className={combined_classes}
      disabled={disabled || is_loading}
      {...props}
    >
      {is_loading ? (
        <>
          <svg 
            className="animate-spin h-4 w-4" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4"
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading...</span>
        </>
      ) : (
        <>
          {icon_left && <span className="flex-shrink-0">{icon_left}</span>}
          {children}
          {icon_right && <span className="flex-shrink-0">{icon_right}</span>}
        </>
      )}
    </motion.button>
  );
};